import React from 'react';
import Grid from '@mui/material/Grid';
import ModalButtons from 'src/components/UI/button/ModalButtons';
import { APICONSTANTS } from 'src/components/Constants';
import axios from 'src/axios';
import { Box, FormControl, Switch, TextField } from '@mui/material';
import {
  SnackContext,
  AppContext,
  ThemeContext,
  UserContext
} from 'src/store/ContextStore';
import {
  getDependencies,
  getClinicalDefaults,
  saveClinicalDefault,
  deleteClinicalDefault
} from './formService';
import TextInput from 'src/components/UI/input/TextInput';
import ClinicalNoteDeleteModal from '../../diagnoses-menu/ClinicalNoteDeleteModal';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import DateInput from 'src/components/UI/input/DateInput';
import { calculateAgeOnDate } from 'src/util/util';
import { toLocalDateFromPickerOrImport } from 'src/util/partialDate';

export default function ConsentForm({
  data,
  handleClose,
  familyMemberId,
  isEditing,
  model,
  staffMembers,
  staffMemberOptions,
  setUnsavedChanges
}) {
  const { content } = React.useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  const { user: loggedInUser } = React.useContext(UserContext);

  const patientDob = data?.dob;
  // The age on the calendar day of the consent (response) date
  // (calculateAgeOnDate). The date is handed over as stored — a picker instant,
  // the server's new Date() for a consent captured by a form submission, or UTC
  // midnight from the spreadsheet importers and demo seed — and only the raw
  // value says which, so it is read by toLocalDateFromPickerOrImport.
  // `calculateAge` counted elapsed time from a UTC-midnight birth date: a year
  // short on the birthday at any positive offset, and a positive age for a date
  // before the birth, which is now blank. The date field shows the day it read
  // (asPickerDay), so the day on screen is the day the age is from.
  const asPickerDay = toLocalDateFromPickerOrImport;
  const computeAgeAtRecord = React.useCallback(
    (dateValue) => calculateAgeOnDate(patientDob, dateValue),
    [patientDob]
  );

  const defaultConsentDate = new Date();
  const defaultValues = isEditing
    ? {
        ...model,
        ageAtRecord: model?.ageAtRecord ?? '',
        reminderEnabled: !!model?.reminderEnabled,
        reminderDate: model?.reminderDate || '',
        obtainedBy: Array.isArray(model.obtainedBy)
          ? model.obtainedBy.map((r) => r.userId || r._id).filter(Boolean)
          : []
      }
    : {
        familyId: user.familyId,
        familyMemberId: familyMemberId,
        consentType: '',
        consentRequestDate: defaultConsentDate,
        consentDate: '',
        consentGivenBy: 'Self',
        response: '',
        showInFamily: false,
        obtainedBy: loggedInUser._id ? [loggedInUser._id] : [],
        consentNote: '',
        // ageAtRecord is computed from the consent RESPONSE date, which
        // starts empty on a new form — so the initial age is empty too.
        ageAtRecord: '',
        reminderEnabled: false,
        reminderDate: ''
      };

  const [formValues, setFormValues] = React.useState(defaultValues);

  const allStaffMembers = React.useMemo(() => {
    return staffMemberOptions.map((options) => options.value);
  }, [staffMemberOptions]);

  const staffMembersMap = React.useMemo(() => {
    return staffMemberOptions.reduce((acc, item) => {
      acc[item.value] = item.label;
      return acc;
    }, {});
  }, [staffMemberOptions]);

  // TGCLOUD-1788 — this was `React.useState([])` with no setter and no fetch, so
  // the Consent Type drop-down had permanently zero options and a value typed in
  // was used for the record being saved and then lost. It is a persisted list
  // like every other clinical default, not a per-form scratch value.
  const [typeOptions, setTypeOptions] = React.useState([]);
  const [responseOptions, setResponseOptions] = React.useState([]);
  const [givenByOptions, setGivenByOptions] = React.useState([]);

  React.useEffect(() => {
    getClinicalDefaults('consentTypes').then(setTypeOptions);
    getClinicalDefaults('consentResponses').then(setResponseOptions);
    getClinicalDefaults('consentGivenBy').then(setGivenByOptions);
  }, []);

  const [error, setError] = React.useState({});

  const handleChange = (e) => {
    setUnsavedChanges((prev) => ({ ...prev, edit: true }));
    if (e.target.type === 'date') {
      const selectedDate = new Date(e.target.value);
      const today = new Date();

      if (selectedDate >= today) {
        setError((prev) => ({ ...prev, [e.target.name]: true }));
      } else setError({});
    }

    // Age at Consent is calculated from the consent RESPONSE date only.
    // The request date is the date a clinician asked for consent, which
    // doesn't necessarily reflect when the patient gave it.
    if (e.target.name === 'consentDate') {
      setFormValues((prevValues) => ({
        ...prevValues,
        ageAtRecord: computeAgeAtRecord(e.target.value)
      }));
    }

    const value =
      e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormValues((prevValues) => ({
      ...prevValues,
      [e.target.name]: value
    }));
  };
  React.useEffect(() => {
    if (!patientDob) return;
    // Calculate from the response date only (see handleChange comment).
    const nextAge = computeAgeAtRecord(formValues.consentDate);
    setFormValues((prev) => {
      const currentAge = prev.ageAtRecord === undefined ? '' : prev.ageAtRecord;
      if (nextAge === '' && currentAge === '') return prev;
      if (nextAge === currentAge) return prev;
      return { ...prev, ageAtRecord: nextAge };
    });
  }, [patientDob, computeAgeAtRecord, formValues.consentDate]);

  const { setSnack } = React.useContext(SnackContext);
  const { theme } = React.useContext(ThemeContext);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const handleCloseModal = () => {
    setOpen(false);
  };
  const handleOpenModal = () => {
    setOpen(true);
  };

  const deleteNote = async (e) => {
    setLoading(true);

    const dep = await getDependencies(
      user.familyId,
      model._id,
      theme.dateFormat
    );
    if (dep.length !== 0) {
      setSnack({
        status: true,
        msg: `Associated record exists; check the ${dep[0]}`,
        severity: 'error'
      });
      setLoading(false);

      return;
    }
    axios
      .delete(`/clinical-notes/note`, {
        params: {
          _id: model._id
        }
      })
      .then((res) => {
        setSnack({
          status: true,
          msg: 'Record deleted',
          severity: 'success'
        });
        handleCloseModal();
        handleClose({ exit: true });
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: `Delete failed! ${err}`,
          severity: 'error'
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const validateForm = (values) => {
    let errors = {};

    if (!values.consentType) {
      errors.consentType = 'Consent Type is required.';
    }

    if (!values.consentRequestDate && !values.consentDate) {
      errors.consentRequestDate =
        'Consent Request Date or Consent Response Date must be provided.';
      errors.consentDate =
        'Consent Request Date or Consent Response Date must be provided.';
    }

    if (values.response) {
      if (!values.consentDate) {
        errors.consentDate =
          'Consent Response Date is required when a response is provided.';
      }
      if (!values.consentGivenBy) {
        errors.consentGivenBy =
          'Consent Given By is required when a response is provided.';
      }
    }

    setError(errors);
    return errors;
  };

  //Think about this
  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const errors = validateForm(formValues);

    if (Object.keys(errors).length > 0) return Promise.reject();

    const obtainedByPayload =
      formValues.obtainedBy && formValues.obtainedBy.length > 0
        ? formValues.obtainedBy
            .map((id) => staffMembers.find((member) => member.userId === id))
            .filter(Boolean)
        : [];

    const parsePayload = {
      ...formValues,
      consentDate: formValues.consentDate || null,
      consentRequestDate: formValues.consentRequestDate || null,
      reminderDate: formValues.reminderDate || null,
      obtainedBy: obtainedByPayload
    };

    return axios
      .put(APICONSTANTS.consent, parsePayload)
      .then((res) => {
        handleClose({ exit: true });
        if (res.status === 200) {
          setSnack({
            status: true,
            msg: 'Successfully saved',
            severity: 'success'
          });
        } else {
          setSnack({
            status: true,
            msg: `Upload failed!`,
            severity: 'error'
          });
        }
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: `Upload failed! ${err}`,
          severity: 'error'
        });
      });
  };

  return (
    //Should be fine to reuse old class?

    <form className="user-form">
      <ClinicalNoteDeleteModal
        loading={loading}
        open={open}
        handleCloseModal={handleCloseModal}
        onConfirm={deleteNote}
        title="Are you sure you want to delete this Consent ?"
        content="Warning: This action will permanently delete this Consent. Once deleted, this
        information will be permanently removed and cannot be restored."
      />
      <Grid container spacing={2}>
        <Grid size={6}>
          <TextInput
            required
            label="Consent Type"
            type="autocomplete"
            name="consentType"
            value={formValues.consentType}
            onChange={(e) => {
              const label = e ? e.label : '';
              handleChange({ target: { name: 'consentType', value: label } });
              // Persist the value as well as using it, so it is offered on the
              // next consent record. saveClinicalDefault is a no-op for a label
              // already in the list.
              if (label) {
                saveClinicalDefault('consentTypes', label).then(setTypeOptions);
              }
            }}
            options={typeOptions || []}
            onDelete={(option) => {
              // Removing an option only stops it being offered in future —
              // records already carrying the value keep it.
              deleteClinicalDefault('consentTypes', option.label).then(
                setTypeOptions
              );
            }}
            validated={!!error.consentType}
            helperText={error.consentType}
          />
        </Grid>

        <Grid size={6}>
          <TextInput
            label={'Response'}
            type="autocomplete"
            variant="outlined"
            fullWidth
            onChange={(e) => {
              const label = e ? e.label : '';
              handleChange({ target: { name: 'response', value: label } });
              // Persist the value as well as using it, so it is offered on
              // the next record. This picker has always shown privileged roles
              // an Add row; until now the value it produced was written to the
              // record and then lost, so the same clinician had to retype it
              // every time (TGCLOUD-1784).
              if (label) {
                saveClinicalDefault('consentResponses', label).then(
                  setResponseOptions
                );
              }
            }}
            onDelete={(option) => {
              deleteClinicalDefault('consentResponses', option.label).then(
                setResponseOptions
              );
            }}
            options={responseOptions || []}
            name="response"
            value={formValues.response}
          />
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel required>Consent Request Date</FormLabel>
          </Grid>
          <DateInput
            value={formValues.consentRequestDate}
            disableFuture
            onChange={(val) =>
              handleChange({
                target: { name: 'consentRequestDate', value: val, type: 'date' }
              })
            }
            variant="outline"
            liveText
            error={!!error?.consentRequestDate}
            helperText={error?.consentRequestDate}
          />
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Consent Response Date</FormLabel>
          </Grid>
          <DateInput
            value={asPickerDay(formValues.consentDate)}
            disableFuture
            onChange={(val) =>
              handleChange({
                target: { name: 'consentDate', value: val, type: 'date' }
              })
            }
            liveText={true}
            variant="outline"
            error={!!error?.consentDate}
            helperText={error?.consentDate}
          />
        </Grid>
        <Grid size={6}>
          <TextInput
            label="Age at Consent"
            name="ageAtRecord"
            type="number"
            value={formValues.ageAtRecord ?? ''}
            inputProps={{ min: 0, className: 'no-spinners' }}
            // Disabled (muted) only when age is actually being
            // auto-calculated, i.e. DoB AND consent response date both set.
            // Editable when either is missing so the clinician can record a
            // known age directly.
            disabled={!!patientDob && !!formValues.consentDate}
            onChange={
              patientDob && formValues.consentDate
                ? undefined
                : (e) => {
                    const val = e.target.value;
                    const parsed = val === '' ? '' : Number(val);
                    if (val === '' || !Number.isNaN(parsed)) {
                      setFormValues((prev) => ({
                        ...prev,
                        ageAtRecord: val === '' ? '' : parsed
                      }));
                      setUnsavedChanges((prev) => ({ ...prev, edit: true }));
                    }
                  }
            }
          />
        </Grid>

        <Grid size={6}>
          <TextInput
            label={'Consent Given By'}
            type="autocomplete"
            fullWidth={true}
            variant="outlined"
            name="consentGivenBy"
            options={givenByOptions || []}
            value={formValues.consentGivenBy || ''}
            onChange={(e) => {
              const label = e ? e.label : '';
              handleChange({
                target: { name: 'consentGivenBy', value: label }
              });
              // Persist the value as well as using it, so it is offered on
              // the next record. This picker has always shown privileged roles
              // an Add row; until now the value it produced was written to the
              // record and then lost, so the same clinician had to retype it
              // every time (TGCLOUD-1784).
              if (label) {
                saveClinicalDefault('consentGivenBy', label).then(
                  setGivenByOptions
                );
              }
            }}
            onDelete={(option) => {
              deleteClinicalDefault('consentGivenBy', option.label).then(
                setGivenByOptions
              );
            }}
            validated={error?.consentGivenBy}
            helperText={error?.consentGivenBy ?? 'Date cannot be in the future'}
          />
        </Grid>

        {/* ObtainedBy */}
        <Grid size={12}>
          <FormControl component="fieldset" fullWidth>
            <Grid container spacing={1}>
              <Grid size={12}>
                <TextInput
                  label={'Obtained By'}
                  name="obtainedBy"
                  type="multiple-select"
                  value={formValues?.obtainedBy || []}
                  onChange={handleChange}
                  options={allStaffMembers || []}
                  mapper={staffMembersMap || {}}
                />
              </Grid>
            </Grid>
          </FormControl>
        </Grid>

        <Grid size={12}>
          <TextInput
            label={'Consent Note'}
            variant="outlined"
            fullWidth
            onChange={handleChange}
            name="consentNote"
            value={formValues.consentNote}
            multiline={true}
            minRows={2}
          />
        </Grid>

        <Grid
          container
          spacing={2}
          size={12}
          sx={{
            alignItems: 'center'
          }}
        >
          <Grid
            size={{
              xs: 12,
              sm: 'auto'
            }}
          >
            <FormLabel>Reminder</FormLabel>
          </Grid>
          <Grid>
            <Switch
              name="reminderEnabled"
              color="secondary"
              checked={!!formValues.reminderEnabled}
              onChange={handleChange}
            />
          </Grid>
          {formValues.reminderEnabled && (
            <Grid
              sx={{ marginLeft: 14 }}
              size={{
                xs: 12,
                sm: 6
              }}
            >
              <Grid sx={{ paddingBottom: 1 }}>
                <FormLabel>Reminder Date</FormLabel>
              </Grid>
              <DateInput
                name="reminderDate"
                value={formValues.reminderDate}
                onChange={(val) =>
                  handleChange({
                    target: { name: 'reminderDate', value: val, type: 'date' }
                  })
                }
                variant="outline"
                error={!!error?.reminderDate}
                helperText={error?.reminderDate}
              />
            </Grid>
          )}
        </Grid>

        <ModalButtons
          isEditing={isEditing}
          hideContinueAdding={true}
          onClick={handleSubmit}
          handleClose={handleClose}
          addLabel={`Save new consent`}
          intermediateBtnLabel="Delete"
          intermediateAction={isEditing ? handleOpenModal : null}
        />
      </Grid>
    </form>
  );
}
